// src/features/tasks/components/TaskListPage/TasksGrid.jsx
import React from 'react';
import {
  Box,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import TaskCard from '../TaskCard';
import TasksEmptyState from './TasksEmptyState';
import TasksDataGrid from './TasksDataGrid';
import { useTasksDataGrid } from './TasksDataGrid/hooks/useTasksDataGrid';

export default function TasksGrid({
  tasks,
  viewMode,
  loading,
  currentFilter,
  canCreate,
  canUpdate,
  canDelete,
  canChangeState,
  onView,
  onEdit,
  onDelete,
  onStateChange,
  onCreateClick,
}) {
  const { t } = useTranslation(['tasks']);
  const { getTaskSortPriority } = useTasksDataGrid(tasks);

  // Render DataGrid for list view (loading and empty state handled inside DataGrid)
  if (viewMode === 'list') {
    return (
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <TasksDataGrid
          tasks={tasks}
          loading={loading}
          canUpdate={canUpdate}
          canDelete={canDelete}
          canChangeState={canChangeState}
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
          onStateChange={onStateChange}
          currentFilter={currentFilter}
          canCreate={canCreate}
          onCreateClick={onCreateClick}
        />
      </Box>
    );
  }

  // Show empty state only for card view when no tasks
  if (tasks.length === 0) {
    return (
      <TasksEmptyState
        currentFilter={currentFilter}
        canCreate={canCreate}
        onCreateClick={onCreateClick}
      />
    );
  }

  // Render card grid for cards view with sorting
  const sortedTasks = [...tasks].sort((a, b) => {
    const priorityA = getTaskSortPriority(a);
    const priorityB = getTaskSortPriority(b);
    
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }
    
    // Secondary sort by due_date
    const dateA = a.due_date ? new Date(a.due_date) : new Date(8640000000000000);
    const dateB = b.due_date ? new Date(b.due_date) : new Date(8640000000000000);
    return dateA - dateB;
  });

  return (
    <Box
      display="grid"
      gridTemplateColumns="repeat(auto-fill, minmax(350px, 1fr))"
      gap={3}
    >
      {sortedTasks.map((task) => (
        <TaskCard
          key={task.task_id}
          task={task}
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
          onStateChange={onStateChange}
          canEdit={canUpdate}
          canDelete={canDelete}
          canChangeState={canChangeState}
          showDescriptionOnHover={true}
        />
      ))}
    </Box>
  );
}